import React, { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import UserLocator from "../data/UserLocator";
import variables from "@/common/globalVariables.js";
import appText from "@/lang/appText";
import PageSection from "@/common/Layout/PageSection";
import Container from "@/common/Layout/Container";
import Title from "@/common/UI/Title";
import DynamicImageBg from "@/common/UI/DynamicImageBg";
import SearchArea from "@/components/jobs/Search/SearchArea";
import { basePath } from "@/root/config";
import JobsCards from "@/components/jobs/JobList/JobsListQuery";
import PopularTerms from "@/components/jobs/PopularTerms/PopularTerms";
import { getJobsFromAPI, getTermsFromAPI } from "@/lib/backend";
import Translator, { ListOfLanguages } from "@/components/hoc/Translator";
import { MdOutlineLanguage } from "react-icons/md";
import { FaLightbulb } from "react-icons/fa";
import { TiTick } from "react-icons/ti";

const landingLogo = "/images/LandingLogo.svg";

const FREE_AI_SMS_TO = "20202";
const FREE_AI_SMS_BODY = "READY";

const buildFreeAiSmsHref = () => {
  if (typeof navigator === "undefined") {
    return `sms:${FREE_AI_SMS_TO}?body=${encodeURIComponent(FREE_AI_SMS_BODY)}`;
  }
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const isIOSDevice =
    /iPad|iPhone|iPod/.test(ua) ||
    (platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const useAppleSmsQuery =
    isIOSDevice || platform.startsWith("Mac") || platform === "iPhone";
  const q = encodeURIComponent(FREE_AI_SMS_BODY);
  return useAppleSmsQuery
    ? `sms:${FREE_AI_SMS_TO}&body=${q}`
    : `sms:${FREE_AI_SMS_TO}?body=${q}`;
};

// const homePageStyle = `background: linear-gradient(0deg, white 40%, ${variables.mutedColor1} 40%);`;

const homePage = (props) => {
  // return <p>Hello</p>;
  const [userLocation, setUserLocation] = useState({
    name: "",
    lat: 0,
    lon: 0,
  });
  const [freeAiSmsHref, setFreeAiSmsHref] = useState(() =>
    `sms:${FREE_AI_SMS_TO}?body=${encodeURIComponent(FREE_AI_SMS_BODY)}`
  );

  const userLocator = new UserLocator();

  useEffect(() => {
    setFreeAiSmsHref(buildFreeAiSmsHref());
  }, []);

  useEffect(() => {
    userLocator.getLocation().then((res) => {
      setUserLocation(res);
    });
  }, []);

  return (
    <PageSection className="HomePage" column nopadding data-test="indexPage">
      <SEO
        description={`${appText.seo.pages.landing.description}. ${appText.seo.description}! Discover remote jobs, temp work, full-time careers, and high-paying gigs near you. Search faster on one of the best job boards for 2025.`}
        title={`${appText.seo.title}! Remote Gigs, Executive Careers & Jobs Hiring Near You`}
      />
      <DynamicImageBg
        staticImage
        query={
          userLocation.name !== "Loading..."
            ? userLocation.country || userLocation.name
            : "Los Angeles, CA"
        }
      >
        <div className="frame-box">
          <Container>
            {/* <div className="Logos">
              <img src={landingLogo} className="CompanyLogo" />
            </div> */}
            <section className="hero">
              <h1>
                From Gigs to Executive Roles
                <br />
                Find Flexible Work Across Every Industry
              </h1>

              <p className="hero-description">
                Discover temporary jobs, gig work, full-time careers, and
                executive roles across industries – from IT,
                <br />
                healthcare, accounting, sales, warehouse & more.
              </p>

              {/* include this in seo but don't show in UI */}
              <div className="hashtag-line">
                #JobSearch • #NowHiring • #JobsHiring • #RemoteJobs •
                #FlexibleWork • #GigJobs • #TempJobs • #HiringAlert •
                #WorkFromHome • #JobBoard • #CareerGoals • #JobHunt •
                #FullTimeJobs • #PartTimeJobs • #ExecutiveJobSearch •
                #StaffingSolutions • #JobSeeker • #HighPayingJobs •
                #EntryLevelJobs • #BestJobBoards2025 • #JobSearchWebsites •
                #JobBoardsForRemoteWork TorranceJobs AlbanyJobs ValenciaJobs
                OnlineJobs JobSearch NowHiring RemoteJobs JobsNearMe Flexible
                Work FullTimeJobs GigJobs TempJobs PartTimeJobs Jobs
                ExecutiveJobSearch Job Openings Job Hunt CareerGoals
                MarketingJobs ITJobs MaderaJobs BakersfieldJobs FresnoJobs
                JobsNewarkDE ExactStaffJobs WarehouseJobs ForkliftJobs
                ShafterJobs CamarilloJobs ArvinJobs CalabasasJobs LosAngelesJobs
                SacramentoJobs OxnardJobs StaffingJobs SalesJobs AccountingJobs
                HealthcareJobs
              </div>

              <SearchArea location={userLocation.name} />
            </section>
          </Container>
        </div>
        <section
          className="free-ai-banner-wrap"
          aria-label="Free AI training — text READY to 20202"
        >
          <Container>
            <a
              href={freeAiSmsHref}
              className="free-ai-banner-link"
            >
              <img
                src="/banners/WebsiteBanner_FreeAI.1.jpg"
                alt="Promotional banner: Free AI training for Exact Staff associates through the U.S. Department of Labor. Tap to open messages with READY ready to send to 20202."
                className="free-ai-banner-img"
                width={1920}
                height={700}
                loading="lazy"
                decoding="async"
              />
            </a>
          </Container>
        </section>
        {/* Blog CTA Section */}
        <div className="blog-cta-section">
          <div className="blog-cta-content">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div className="blog-cta-icon">
                {/* Inline SVG for green light bulb icon */}
                <FaLightbulb
                  style={{
                    color: "#1AAF4B",
                    fontSize: "25px",
                    marginRight: "8px",
                    marginTop: "15px",
                  }}
                />
              </div>
              <h2 className="blog-cta-title">Insider Advantage</h2>
            </div>

            <p className="blog-cta-desc">
              Visit the Exact Staff Insight Blog for complementary career
              advice, AI & technology, hiring trends, plus more!
            </p>
            <div className="blog-cta-checks">
              <div>
                <TiTick
                  style={{
                    color: "#1AAF4B",
                    fontSize: "24px",
                    marginRight: "8px",
                  }}
                />
                Learn Something New
              </div>
              <div>
                <TiTick
                  style={{
                    color: "#1AAF4B",
                    fontSize: "24px",
                    marginRight: "8px",
                  }}
                />
                Find Your Edge
              </div>
              <div>
                <TiTick
                  style={{
                    color: "#1AAF4B",
                    fontSize: "24px",
                    marginRight: "8px",
                  }}
                />
                Grow Your Career
              </div>
            </div>
            <a
              href="https://exactstaff.com/blogs/"
              target="_blank"
              rel="noopener noreferrer"
              className="blog-cta-btn"
            >
              Explore the Blog Now
            </a>
          </div>
        </div>
      </DynamicImageBg>
      <Container>
        {/* langualge toggler */}
        {/* <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
          title="Change language"
        >
          <MdOutlineLanguage
            size={30}
            color="green"
            style={{
              marginRight: "5px",
            }}
          />
          <ListOfLanguages />
        </div> */}
        <Title size={"l"} center margin capitalize>
          <Translator>{appText.messages.whatsTrending} 😎</Translator>
        </Title>
        <PopularTerms terms={props.terms} />
        <Title size={"l"} center margin capitalize>
          <Translator>{appText.messages.job.latest}</Translator>
        </Title>
        <JobsCards jobs={props.jobs} />
      </Container>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: `{
              "@context": "https://schema.org",
              "@type": "WebSite",
              "url": "${basePath}/",
              "potentialAction": {
                "@type": "SearchAction",
                "target": "${basePath}/jobs?q={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            }`,
        }}
      />
      <style jsx>{`
        .frame-box {
          width: 100%;
          aspect-ratio: 3 / 1; /* Maintain shape ratio */
          margin: 0 auto 3rem;
          padding: 4% 6%; /* padding from edge of image so content stays inside frame */
          background-image: url("/images/2400_800_BorderMyExactJobs_SEO.png");
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          box-sizing: border-box;
          text-align: center;
          position: relative;
        }

        .Logos {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          width: 100%;
          max-width: 500px;
          padding: 20px 30px;
          margin: auto;
        }

        .Logos img {
          width: 100%;
        }

        .CompanyLogo {
          margin-bottom: 15px;
        }

        .PeopleLogo {
          width: 100%;
          max-width: 460px;
        }

        .PeopleLogo img {
          width: 100%;
        }

        @media (max-width: ${variables.mediumScreen}) {
          .Logos,
          .PeopleLogo,
          .CompanyLogo {
             {
              /* display: none; */
            }
          }
        }
        @media (max-width: 1250px) {
          .frame-box {
             {
              /* padding: 50px 0px; */
            }
            aspect-ratio: 3 / 1; /* Maintain shape ratio */
            margin: 0 auto 3rem;
            background-image: url("/images/2400_800_BorderMyExactJobs_SEO.png");
            background-size: cover;
            background-repeat: no-repeat;
            background-position: center;
          }
        }

        .hero {
          text-align: center;
           {
            /* padding: 80px 20px 30px; */
          }
        }
        .hero h1 {
          font-size: 2.2em;

          font-weight: bold;

          margin: 0;

          line-height: 1.3;

          color: #222;
        }
        .hero p {
          font-size: 1.5em;

          color: #555;

          margin: 15px auto;

          max-width: 700px;
        }

        @media (max-width: 700px) {
          .Logos {
            display: none;
          }
          .frame-box {
            background-image: url("/images/Mobile1080x1350_BorderMyExactJobs_SEO.png");
            background-size: contain;
            aspect-ratio: 4 / 5; /* Adjust to wrap around content */
             {
              /* padding: 20px 40px; */
            }
          }
          .hero h1 {
            font-size: 1.1em;
          }
          .hero p {
            font-size: 0.8em;
          }
        }

        .hashtag-line {
          font-size: 0.8em;
          color: #555;
          margin: 25px auto 35px;
          max-width: 700px;
          display: none; /* hide this in UI but keep it in SEO */
        }
        .free-ai-banner-wrap {
          width: 100%;
          margin: 0;
          padding: 1.25rem 0 1.75rem;
          background: linear-gradient(
            180deg,
            ${variables.mutedColor1} 0%,
            #ffffff 100%
          );
          border-top: 1px solid rgba(46, 41, 78, 0.06);
          border-bottom: 1px solid rgba(46, 41, 78, 0.06);
          box-sizing: border-box;
        }
        .free-ai-banner-wrap :global(.Container) {
          padding-top: 0;
        }
        .free-ai-banner-link {
          display: block;
          width: 100%;
          max-width: 100%;
          margin: 0 auto;
          line-height: 0;
          border-radius: 14px;
          overflow: hidden;
          box-shadow:
            0 4px 6px -1px rgba(46, 41, 78, 0.06),
            0 12px 28px -8px rgba(46, 41, 78, 0.14);
          transition:
            box-shadow 0.25s ease,
            transform 0.25s ease;
        }
        .free-ai-banner-link:hover {
          box-shadow:
            0 8px 12px -2px rgba(46, 41, 78, 0.08),
            0 20px 40px -12px rgba(26, 175, 75, 0.18);
          transform: translateY(-2px);
        }
        .free-ai-banner-link:focus {
          outline: none;
        }
        .free-ai-banner-link:focus-visible {
          outline: 3px solid ${variables.accentColor1};
          outline-offset: 4px;
        }
        .free-ai-banner-img {
          width: 100%;
          height: auto;
          display: block;
          vertical-align: top;
        }
        @media (max-width: 700px) {
          .free-ai-banner-wrap {
            padding: 1rem 0 1.25rem;
          }
        }
        .blog-cta-section {
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 2rem 0 2.5rem 0;
        }
        .blog-cta-content {
          text-align: center;
          max-width: 800px;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .blog-cta-title {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: #3a3a3a;
        }
        .blog-cta-desc {
          font-size: 1rem;
          color: #222;
          margin-bottom: 1.1rem;
          font-weight: 550;
        }
        .blog-cta-checks {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1.3rem;
          font-size: 1.05rem;
          color: #222;
          font-weight: 500;
        }
        .blog-cta-checks > div {
          display: flex;
          align-items: center;
        }
        .green-check {
          color: #4fd165;
          font-size: 1.2em;
          margin-right: 0.5em;
        }
        .blog-cta-btn {
          display: inline-block;
          background: #4fd165;
          color: #fff;
          font-size: 1.15rem;
          font-weight: 700;
          border-radius: 8px;
          padding: 0.9rem 2.2rem;
          text-decoration: none;
          box-shadow: 0 2px 8px 0 rgba(79, 209, 101, 0.13);
          transition: background 0.2s;
        }
        .blog-cta-btn:hover {
          background: #2eae4d;
        }
        @media (max-width: 700px) {
          .blog-cta-content {
            padding: 1.2rem 0.7rem 1.5rem 0.7rem;
          }
          .blog-cta-title {
            font-size: 1.1rem;
          }
          .blog-cta-desc {
            font-size: 0.95rem;
          }
          .blog-cta-checks {
            font-size: 0.9rem;
            gap: 0.7rem;
          }
          .blog-cta-btn {
            font-size: 1rem;
            padding: 0.7rem 1.2rem;
          }
        }
      `}</style>
    </PageSection>
  );
};

export async function getStaticProps() {
  const jobs = await getJobsFromAPI();
  const terms = await getTermsFromAPI();

  return { props: { jobs, terms }, revalidate: 1 };
}

export default homePage;
